import { arrayRemove } from "firebase/firestore";
import React from "react";
import {
    Button,
    ButtonGroup,
    Card,
    Dropdown,
    ListGroup,
    Spinner,
} from "react-bootstrap";
import { FaCode, FaDatabase, FaEdit, FaMap, FaTrash } from "react-icons/fa";
import { MdPower, MdPowerOff } from "react-icons/md";
import { Link, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import { getFirebaseConsoleUrl } from "../../api/firestoreBaseApi.jsx";
import {
    DockDocument,
    DroneConfigDocument,
    useDockDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import DevMenuButton from "../../components/DevMenuButton";
import Layout, { Title } from "../../components/Layout/Layout";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";

export default function DockPage() {
    const { organizationId, locationId, dockId } = useParams();
    const [dock] = useDockDocument(organizationId, locationId, dockId);
    const isLoading = dock == null;
    const [dockDroneIds] = api.useDockDroneIds(
        organizationId,
        locationId,
        dockId,
    );
    const navigate = useNavigate();
    const { getUserIsAdmin } = useUser();
    const [view, setView] = React.useState("default");
    const calibrationPoint =
        dock?.position?.x != null &&
        dock?.position?.y != null &&
        dock?.position?.z != null &&
        dock?.calibrationPointOffset?.x != null &&
        dock?.calibrationPointOffset?.y != null &&
        dock?.calibrationPointOffset?.z != null &&
        dock?.takeoffHeight != null
            ? {
                  x: Number(
                      (
                          Number(dock.position.x) +
                          Number(dock.calibrationPointOffset.x)
                      ).toFixed(2),
                  ),
                  y: Number(
                      (
                          Number(dock.position.y) +
                          Number(dock.calibrationPointOffset.y)
                      ).toFixed(2),
                  ),
                  z: Number(
                      (
                          Number(dock.position.z) +
                          Number(dock.takeoffHeight) +
                          Number(dock.calibrationPointOffset.z)
                      ).toFixed(2),
                  ),
              }
            : undefined;

    const returnPoint =
        dock?.position?.x != null &&
        dock?.position?.y != null &&
        dock?.position?.z != null &&
        dock?.returnPointOffset?.x != null &&
        dock?.returnPointOffset?.y != null &&
        dock?.returnPointOffset?.z != null &&
        dock?.takeoffHeight != null
            ? {
                  x: Number(
                      (
                          Number(dock.position.x) +
                          Number(dock.returnPointOffset.x)
                      ).toFixed(2),
                  ),
                  y: Number(
                      (
                          Number(dock.position.y) +
                          Number(dock.returnPointOffset.y)
                      ).toFixed(2),
                  ),
                  z: Number(
                      (
                          Number(dock.position.z) +
                          Number(dock.takeoffHeight) +
                          Number(dock.returnPointOffset.z)
                      ).toFixed(2),
                  ),
              }
            : undefined;

    async function onDeleteDock() {
        try {
            // Delete the dock document
            await DockDocument(organizationId, locationId, dockId).delete();

            // Remove the dock id from droneConfigs
            await Promise.all(
                dockDroneIds.map((droneId) => {
                    console.log(
                        `Removing dock ${dockId} from drone ${droneId}`,
                    );
                    return DroneConfigDocument(droneId).update({
                        docks: arrayRemove({ id: dockId }),
                    });
                }),
            );
        } catch (err) {
            console.error("Error deleting dock or cleaning up configs", err);
        } finally {
            // Navigate back to docks page
            navigate(urls.docks(organizationId, locationId));
        }
    }

    function onShowDockOnMap() {
        const base = urls.locationMap(organizationId, locationId);
        const params = new URLSearchParams({ highlightedDockId: dockId });
        if (getUserIsAdmin()) {
            params.set("inspect", true);
        }
        navigate(`${base}?${params.toString()}`);
    }

    function visitFirestore() {
        window.open(
            getFirebaseConsoleUrl(
                DockDocument(organizationId, locationId, dockId).path,
            ),
        );
    }

    return (
        <Layout>
            <title>{`Dock ${dock?.name ?? ""} | MyCorvus`}</title>
            <Breadcrumbs path={breadcrumbPaths.dock} dock={dock ?? null} />
            <Card>
                <Title
                    backButtonUrl={urls.docks(organizationId, locationId)}
                    title={`Dock ${dock?.name ?? ""}`}
                >
                    {getUserIsAdmin() && (
                        <DevMenuButton className="ms-1 mb-1">
                            <Dropdown.Item
                                onClick={() =>
                                    setView(
                                        view === "default" ? "data" : "default",
                                    )
                                }
                            >
                                <FaCode
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                {view === "default"
                                    ? "View Data"
                                    : "View Default"}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={visitFirestore}>
                                <FaDatabase
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                View Firestore
                            </Dropdown.Item>
                        </DevMenuButton>
                    )}
                    <Dropdown as={ButtonGroup} className="ms-1 mb-1">
                        <Button
                            variant="outline-secondary"
                            as={Link}
                            to={urls.dockSettings(
                                organizationId,
                                locationId,
                                dockId,
                            )}
                        >
                            <FaEdit
                                className="me-2"
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                            Edit
                        </Button>
                        <Dropdown.Toggle
                            split
                            variant="outline-secondary"
                            id="dropdown-split-basic"
                        />
                        <Dropdown.Menu align="end">
                            <Dropdown.Item
                                onClick={onShowDockOnMap}
                                variant="outline-danger"
                            >
                                <FaMap
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Show on map
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={onDeleteDock}
                                variant="outline-danger"
                                className="text-danger"
                            >
                                <FaTrash
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Delete
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Title>
                {isLoading ? (
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                ) : (
                    <>
                        {view === "data" && (
                            <Card.Body className="p-4 pt-0">
                                <CodeBlock name="Dock" code={dock} />
                            </Card.Body>
                        )}
                        {view === "default" && (
                            <ListGroup variant="flush">
                                <ListGroup.Item className="px-4">
                                    <b>Powered (only rpv3)</b>
                                    <span className="ms-2">
                                        {dock?.powered === undefined ? (
                                            "Not defined"
                                        ) : dock.powered ? (
                                            <>
                                                Yes
                                                <MdPower className="text-success ms-1" />
                                            </>
                                        ) : (
                                            <>
                                                No
                                                <MdPowerOff className="text-secondary ms-1" />
                                            </>
                                        )}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Position</b>
                                    <span className="ms-2">
                                        {dock?.position?.x !== undefined &&
                                        dock?.position?.y !== undefined &&
                                        dock?.position?.z !== undefined
                                            ? `x: ${dock.position.x}, y: ${dock.position.y}, z: ${dock.position.z}`
                                            : "Not defined"}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Yaw</b>
                                    <span className="ms-2">
                                        {dock?.yaw === undefined
                                            ? "Not defined"
                                            : `${dock.yaw}°`}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Takeoff height (only rpv3)</b>
                                    <span className="ms-2">
                                        {dock?.takeoffHeight === undefined
                                            ? "Not defined"
                                            : `${dock.takeoffHeight} m`}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Calibration point offset (only rpv3)</b>
                                    <span className="ms-2">
                                        {dock?.calibrationPointOffset?.x !==
                                            undefined &&
                                        dock?.calibrationPointOffset?.y !==
                                            undefined &&
                                        dock?.calibrationPointOffset?.z !==
                                            undefined
                                            ? `x: ${dock.calibrationPointOffset.x}, y: ${dock.calibrationPointOffset.y}, z: ${dock.calibrationPointOffset.z}`
                                            : "Not defined"}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Calibration point (only rpv3)</b>
                                    <span className="ms-2">
                                        {calibrationPoint !== undefined
                                            ? `x: ${calibrationPoint.x}, y: ${calibrationPoint.y}, z: ${calibrationPoint.z}`
                                            : "Not defined"}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Return point offset (rpv3.0.5+)</b>
                                    <span className="ms-2">
                                        {dock?.returnPointOffset?.x !==
                                            undefined &&
                                        dock?.returnPointOffset?.y !==
                                            undefined &&
                                        dock?.returnPointOffset?.z !== undefined
                                            ? `x: ${dock.returnPointOffset.x}, y: ${dock.returnPointOffset.y}, z: ${dock.returnPointOffset.z}`
                                            : "Not defined"}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Return point (rpv3.0.5+)</b>
                                    <span className="ms-2">
                                        {returnPoint !== undefined
                                            ? `x: ${returnPoint.x}, y: ${returnPoint.y}, z: ${returnPoint.z}`
                                            : "Not defined"}
                                    </span>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-4">
                                    <b>Drone</b>
                                    <span className="ms-2">
                                        {dockDroneIds.length > 0
                                            ? dockDroneIds.map(
                                                  (dockDrone, index) => (
                                                      <React.Fragment
                                                          key={dockDrone}
                                                      >
                                                          <Link
                                                              to={urls.locationDrone(
                                                                  organizationId,
                                                                  locationId,
                                                                  dockDrone,
                                                              )}
                                                          >
                                                              {dockDrone}
                                                          </Link>
                                                          {index <
                                                              dockDroneIds.length -
                                                                  1 && ", "}
                                                      </React.Fragment>
                                                  ),
                                              )
                                            : "No Drone"}
                                    </span>
                                </ListGroup.Item>
                            </ListGroup>
                        )}
                    </>
                )}
            </Card>
        </Layout>
    );
}
